import { useEffect, useMemo, useState } from "react";
import dealflowLogoTeal from "../images/dealflow.PNG";
import "./PolicyPage.css";

function renderInline(text) {
  const parts = [];
  let remaining = text;
  let key = 0;

  while (remaining.length > 0) {
    const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
    const codeMatch = remaining.match(/`(.+?)`/);

    const match = [boldMatch, codeMatch]
      .filter(Boolean)
      .sort((a, b) => a.index - b.index)[0];

    if (!match) {
      parts.push(<span key={`txt-${key++}`}>{remaining}</span>);
      break;
    }

    if (match.index > 0) {
      parts.push(<span key={`txt-${key++}`}>{remaining.slice(0, match.index)}</span>);
    }

    const isBold = match[0].startsWith("**");
    parts.push(
      isBold ? (
        <strong key={`fmt-${key++}`}>{match[1]}</strong>
      ) : (
        <code key={`fmt-${key++}`}>{match[1]}</code>
      )
    );

    remaining = remaining.slice(match.index + match[0].length);
  }

  return parts;
}

function MarkdownBlock({ content }) {
  const lines = useMemo(() => content.split("\n"), [content]);
  const blocks = [];
  let listItems = [];

  const flushList = (indexKey) => {
    if (listItems.length) {
      blocks.push(
        <ul key={`ul-${indexKey}`}>
          {listItems.map((item, i) => (
            <li key={`li-${indexKey}-${i}`}>{renderInline(item)}</li>
          ))}
        </ul>
      );
      listItems = [];
    }
  };

  lines.forEach((rawLine, index) => {
    const line = rawLine.trimEnd();

    if (line.startsWith("- ")) {
      listItems.push(line.slice(2).trim());
      return;
    }

    flushList(index);

    if (!line.trim()) {
      return;
    }

    if (line === "---") {
      blocks.push(<hr key={`hr-${index}`} />);
      return;
    }

    if (line.startsWith("### ")) {
      blocks.push(<h3 key={`h3-${index}`}>{renderInline(line.slice(4))}</h3>);
      return;
    }

    if (line.startsWith("## ")) {
      blocks.push(<h2 key={`h2-${index}`}>{renderInline(line.slice(3))}</h2>);
      return;
    }

    if (line.startsWith("# ")) {
      blocks.push(<h1 key={`h1-${index}`}>{renderInline(line.slice(2))}</h1>);
      return;
    }

    blocks.push(<p key={`p-${index}`}>{renderInline(line)}</p>);
  });

  flushList("end");

  return <article className="policy-markdown">{blocks}</article>;
}

function PolicyPage({ title, markdownPath }) {
  const [content, setContent] = useState("");
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      try {
        const response = await fetch(markdownPath);
        if (!response.ok) {
          throw new Error(`Failed to load document (${response.status})`);
        }
        const text = await response.text();
        if (isMounted) {
          setContent(text);
        }
      } catch (e) {
        if (isMounted) {
          setError(e.message);
        }
      }
    };

    load();
    return () => {
      isMounted = false;
    };
  }, [markdownPath]);

  return (
    <div className="policy-page">
      <main className="policy-main">
        <img src={dealflowLogoTeal} alt="dealflow" className="policy-top-logo" />

        <div className="policy-card">
          <div className="policy-page-title">{title}</div>

          {error && <p className="policy-error">{error}</p>}
          {!error && !content && <p className="policy-loading">Loading...</p>}
          {!error && content && <MarkdownBlock content={content} />}
        </div>
      </main>

      <footer className="policy-footer">© 2026 Player&apos;s Punch. All rights reserved.</footer>
    </div>
  );
}

export default PolicyPage;
